/**
 * Keeps the screen on while something needs it (1.6.4: the phone recorder; uploads later). Holders are counted:
 * the lock is requested with the first and released with the last. The browser drops the lock whenever the page
 * is hidden, so it is requested again when the page is shown. A browser without the API, or one that refuses
 * (low battery, not allowed), is ignored: the screen then locks as it normally would.
 */
type Sentinel = { released: boolean; release: () => Promise<void>; addEventListener?: (type: 'release', fn: () => void) => void };
type WakeLockApi = { request: (type: 'screen') => Promise<Sentinel> };

let holders = 0;
let sentinel: Sentinel | null = null;
let pending = false;

const api = (): WakeLockApi | undefined => (typeof navigator !== 'undefined' ? (navigator as Navigator & { wakeLock?: WakeLockApi }).wakeLock : undefined);

async function request() {
  const wl = api();
  if (!wl || pending || holders === 0 || (sentinel && !sentinel.released) || document.visibilityState !== 'visible') return;
  pending = true;
  try {
    const s = await wl.request('screen');
    if (holders === 0) { void s.release().catch(() => undefined); return; }
    sentinel = s;
    s.addEventListener?.('release', () => { if (sentinel === s) sentinel = null; });
  } catch { /* refused: ignore */ } finally { pending = false; }
}

function onVisibility() { if (document.visibilityState === 'visible') void request(); }

/** Hold the screen awake until the returned function is called (calling it twice is harmless). */
export function holdWakeLock(): () => void {
  if (!api()) return () => undefined;
  holders += 1;
  if (holders === 1) document.addEventListener('visibilitychange', onVisibility);
  void request();
  let done = false;
  return () => {
    if (done) return;
    done = true;
    holders -= 1;
    if (holders > 0) return;
    document.removeEventListener('visibilitychange', onVisibility);
    const s = sentinel;
    sentinel = null;
    if (s && !s.released) void s.release().catch(() => undefined);
  };
}
